import type { Locale } from '@/i18n'
import type { NavItem } from '@/config/navigation'
import { splitSections, type DocSections } from './markdown'

/**
 * Page docs live in src/docs/<pageKey>/{ru,en,uz}.md. Each file is its own
 * chunk, fetched when someone opens a «?». Ўзбекча is transliterated from
 * O'zbekcha, like the dictionaries.
 */
const files = import.meta.glob<string>('/src/docs/*/*.md', { query: '?raw', import: 'default' })

type FileLocale = 'ru' | 'en' | 'uz'
const fileLocale = (locale: Locale): FileLocale => (locale === 'cyr' ? 'uz' : locale)

/** The docs key of a page: an alias (an old address) shares the docs of the page it stands for. */
export const docsKeyOf = (item: NavItem) => item.docsKey ?? item.id

export const hasDocs = (key: string) => `/src/docs/${key}/ru.md` in files

/**
 * Transliterates the prose of a Markdown file and leaves its machinery alone:
 * inline code, link targets, HTML comments, fence lines and URLs.
 */
async function toCyrillicMarkdown(source: string): Promise<string> {
  const { toCyrillic } = await import('@/i18n/transliterate')
  return source
    .split('\n')
    .map((line) =>
      /^\s*```/.test(line)
        ? line
        : line
            .split(/(`[^`]*`|\]\([^)]*\)|<!--.*?-->|https?:\/\/\S+)/)
            .map((part, index) => (index % 2 ? part : toCyrillic(part)))
            .join(''),
    )
    .join('\n')
}

const cache = new Map<string, Promise<DocSections | null>>()

export function loadDocs(key: string, locale: Locale): Promise<DocSections | null> {
  const cacheKey = `${locale}:${key}`
  let found = cache.get(cacheKey)
  if (!found) {
    const load = files[`/src/docs/${key}/${fileLocale(locale)}.md`] ?? files[`/src/docs/${key}/ru.md`]
    found = !load
      ? Promise.resolve(null)
      : load()
          .then((source) => (locale === 'cyr' ? toCyrillicMarkdown(source) : source))
          .then(splitSections)
          .catch((error: unknown) => {
            // A failed chunk is forgotten, so the next attempt tries again.
            cache.delete(cacheKey)
            throw error
          })
    cache.set(cacheKey, found)
  }
  return found
}
